/**
 * Protected Sales Route Component
 * =================================
 * Wrapper component that protects sales portal routes from unauthorized access
 *
 * Features:
 * - Redirects to login if not authenticated
 * - Shows loading state while checking auth
 * - Renders children if authenticated
 *
 * Usage:
 *   <Route
 *     path="/sales-horse/dashboard"
 *     element={
 *       <ProtectedSalesRoute>
 *         <SalesDashboard />
 *       </ProtectedSalesRoute>
 *     }
 *   />
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, ShieldAlert } from 'lucide-react';
import { useSalesAuth } from '../contexts/SalesAuthContext';

interface ProtectedSalesRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

const ProtectedSalesRoute: React.FC<ProtectedSalesRouteProps> = ({ children, requireAdmin = false }) => {
  const { isAuthenticated, isLoading, user } = useSalesAuth();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#1f4ed1] to-[#2256e5]">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="text-center"
        >
          <Loader2 className="w-12 h-12 text-[#FDB913] animate-spin mx-auto" />
          <p className="mt-4 text-white/80">Verifying access...</p>
        </motion.div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/sales-horse" replace />;
  }

  // Check admin role if required
  if (requireAdmin && user?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#1f4ed1] to-[#2256e5]">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="text-center max-w-md mx-auto p-6"
        >
          <ShieldAlert className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Access Denied</h2>
          <p className="text-white/70 mb-6">
            You don't have permission to access this page. Admin privileges are required.
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-6 py-2 bg-[#FDB913] text-black rounded-lg hover:bg-[#FDB913]/90 transition-colors"
          >
            Go Back
          </button>
        </motion.div>
      </div>
    );
  }

  // Render children if authenticated (and admin if required)
  return <>{children}</>;
};

export default ProtectedSalesRoute;
